import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Download, Eye, PackageCheck, PackageOpen, Plus, Upload } from 'lucide-react';
import * as XLSX from 'xlsx';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import { CountChips } from '../../../../components/ui/CountChips';
import { DataTable } from '../../../../components/ui/DataTable';
import { Button } from '../../../../components/ui/Button';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { useNotice } from '../../../../hooks/useNotice';
import { DeclareReceiptModal } from './modals/DeclareReceiptModal';
import { ReceiveReceiptModal } from './modals/ReceiveReceiptModal';
import { ReceiptsUploadModal } from './modals/ReceiptsUploadModal';
import { fetchReceipts } from '../../../../features/receipts/receiptsThunks';
import { setReceiptFilters } from '../../../../features/receipts/receiptsSlice';
import {
  ALL_FILTER,
  RECEIPT_SOURCE_OPTIONS,
  RECEIPT_STATUS,
  RECEIPT_STATUS_ORDER,
  RECEIPT_STATUS_TONES,
} from '../../../../features/receipts/receiptsConstants';
import {
  buildReceiptsExportRows,
  formatReceiptMoment,
  isOpenReceipt,
  receiptsExportFileName,
  unitsOf,
} from '../../../../features/receipts/receiptsUtils';
import {
  selectCurrentWarehouse,
  selectWarehouseClientOptions,
} from '../../../../features/warehouseContext/warehouseContextSlice';
import { selectCanWms } from '../../../../features/auth/authSelectors';
import { formatQuantity, receiptStatusLabel } from '../../../../utils/inventoryLabels';

const RECEIPTS_PATH = '/warehouse/receipts';

const selectCanDeclare = selectCanWms('inventory:receive');

/**
 * `/warehouse/receipts`: the merchandise coming in.
 *
 * Two acts on the same order, and two buttons: **declaring** what is coming,
 * which moves no stock, and **receiving** what arrived, which is the only thing
 * that writes to the kardex. Who may receive is the server's call — it sends
 * `can_confirm` with the list — and nothing here works it out.
 *
 * The listing is for recognising an order and going in: closing with a
 * shortage, editing, voiding and the printable manifest live on its page.
 */
export const Receipts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { receipts, totalCount, statusCounts, canConfirm, loading, error, filters } = useSelector(
    (state) => state.receipts
  );
  const warehouse = useSelector(selectCurrentWarehouse);
  const clientOptions = useSelector(selectWarehouseClientOptions);
  const canDeclare = useSelector(selectCanDeclare);

  const [searchInput, setSearchInput] = useState(filters.search);
  const [isDeclaring, setIsDeclaring] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [receiving, setReceiving] = useState(null);
  const setNotice = useNotice('receipts-notice');

  // Debounce for search
  useEffect(() => {
    const handler = setTimeout(() => {
      const search = searchInput.trim();
      if (search !== filters.search) dispatch(setReceiptFilters({ search }));
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, dispatch]);

  // Fetch when the warehouse in use or a filter changes
  useEffect(() => {
    dispatch(fetchReceipts());
  }, [
    dispatch,
    warehouse?.id,
    filters.status,
    filters.company,
    filters.source,
    filters.search,
    filters.page,
    filters.rowsPerPage,
  ]);


  const showClient = clientOptions.length > 1;

  // Seen but not usable right now: the buttons stay and say why. Declaring
  // happens in a physical place, so "todas mis bodegas" is not enough.
  const declareBlocker = (() => {
    if (!warehouse) return 'Elige arriba la bodega donde va a llegar la carga';
    if (clientOptions.length === 0) return `${warehouse.name} no tiene clientes con contrato activo`;
    return null;
  })();

  const handleExport = () => {
    const sheet = XLSX.utils.json_to_sheet(buildReceiptsExportRows(receipts));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Recepciones');
    XLSX.writeFile(book, receiptsExportFileName(warehouse));
  };

  const columns = [
    {
      header: 'ORDEN',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-bold font-mono text-ninja-dark dark:text-white">{row.code}</span>
          <span className="text-xs text-text-muted mt-0.5">
            {formatReceiptMoment(row.created_at)}
          </span>
        </div>
      ),
    },
    ...(showClient
      ? [
          {
            header: 'CLIENTE',
            cell: (row) => <span className="text-text-secondary">{row.company_name}</span>,
          },
        ]
      : []),
    {
      // Where it was created: the WMS screens, or a channel syncing its stock.
      header: 'ORIGEN',
      cell: (row) => (
        <ToneBadge tone={row.source === 'wms' ? 'neutral' : 'info'} testId="receipt-source">
          {row.source_display || '-'}
        </ToneBadge>
      ),
    },
    {
      // Who brings it and under which document: the two things somebody at the
      // dock reads off the paper to find the order.
      header: 'PROVEEDOR',
      cell: (row) => (
        <div className="flex flex-col max-w-[240px]">
          <span className="text-ninja-dark dark:text-white truncate">{row.supplier || '-'}</span>
          <span className="text-xs text-text-muted mt-0.5 truncate">
            {row.reference ? `Ref. ${row.reference}` : 'Sin referencia'}
          </span>
        </div>
      ),
    },
    {
      header: 'UBICACIÓN',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-mono text-ninja-dark dark:text-white">
            {row.slot_name || 'Por definir'}
          </span>
          {!warehouse && (
            <span className="text-xs text-text-muted mt-0.5">{row.warehouse_name}</span>
          )}
        </div>
      ),
    },
    {
      header: 'ESTADO',
      cell: (row) => (
        <ToneBadge tone={RECEIPT_STATUS_TONES[row.status]} testId="receipt-status">
          {receiptStatusLabel(row.status)}
        </ToneBadge>
      ),
    },
    {
      header: 'UNIDADES',
      cell: (row) => {
        const { expected, received } = unitsOf(row);
        const lines = row.lines?.length || 0;
        return (
          <div className="flex flex-col">
            <span className="font-semibold tabular-nums">
              {formatQuantity(received)} / {formatQuantity(expected)}
            </span>
            <span className="text-xs text-text-muted mt-0.5">
              {lines} línea{lines === 1 ? '' : 's'}
            </span>
          </div>
        );
      },
    },
    {
      header: 'ACCIONES',
      cell: (row) => (
        <div className="flex items-center justify-end gap-1">
          {canConfirm && isOpenReceipt(row) && (
            <Button
              variant="subtle"
              size="sm"
              iconOnly
              icon={PackageCheck}
              title="Recepcionar: contar lo que llegó"
              aria-label={`Recepcionar ${row.code}`}
              data-testid="receipt-receive"
              className="hover:text-ninja-green"
              onClick={() => setReceiving(row)}
            />
          )}
          <Button
            variant="subtle"
            size="sm"
            iconOnly
            icon={Eye}
            title="Ver detalle"
            aria-label={`Ver ${row.code}`}
            data-testid="receipt-view"
            onClick={() => navigate(`${RECEIPTS_PATH}/${row.id}`)}
          />
        </div>
      ),
    },
  ];

  const chipOptions = RECEIPT_STATUS_ORDER.map((status) => ({
    value: status,
    label: receiptStatusLabel(status),
    count: statusCounts?.[status],
    tone:
      // Pending is "it is your turn", which the grey of its badge does not say.
      status === RECEIPT_STATUS.PENDING ? 'info' : RECEIPT_STATUS_TONES[status],
  }));
  const chipsTotal = statusCounts
    ? Object.values(statusCounts).reduce((total, count) => total + count, 0)
    : null;

  // An empty table has to say why: empty looks the same as broken.
  const isFiltering = filters.search !== '' || filters.company !== ALL_FILTER || filters.source !== ALL_FILTER;
  const emptyMessage = (() => {
    if (filters.status !== ALL_FILTER) {
      return `No hay recepciones en estado "${receiptStatusLabel(filters.status)}".`;
    }
    if (isFiltering) return 'No hay recepciones que coincidan con estos filtros.';
    return `No hay recepciones todavía en ${
      warehouse ? warehouse.name : 'tus bodegas'
    }. Declara la primera para avisar qué mercadería viene.`;
  })();

  return (
    <div data-testid="receipts-page" className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={PackageOpen}
        title="Recepción"
        description="Se declara lo que viene; el stock entra recién cuando alguien de la bodega cuenta lo que llegó, y queda en la zona de recepción hasta que se guarde."
        actions={
          canDeclare && (
            <Button
              variant="primary"
              icon={Plus}
              disabled={Boolean(declareBlocker)}
              title={declareBlocker || undefined}
              onClick={() => setIsDeclaring(true)}
              data-testid="receipt-add"
            >
              Nueva orden de recepción
            </Button>
          )
        }
      />

      <CountChips
        options={chipOptions}
        value={filters.status}
        onChange={(status) => dispatch(setReceiptFilters({ status }))}
        allValue={ALL_FILTER}
        total={chipsTotal}
        testId="receipts-status"
      />

      <FilterBar
        variant="bare"
        testId="receipts-filters"
        actions={
          <>
            {canDeclare && (
              <Button
                variant="ghost"
                icon={Upload}
                disabled={Boolean(declareBlocker)}
                title={declareBlocker || undefined}
                onClick={() => setIsUploading(true)}
                data-testid="receipts-upload"
              >
                Carga masiva
              </Button>
            )}
            <Button
              variant="ghost"
              icon={Download}
              onClick={handleExport}
              disabled={receipts.length === 0}
              title={
                receipts.length > 0
                  ? 'Excel de las recepciones que estás viendo'
                  : 'No hay recepciones que exportar'
              }
              data-testid="receipts-export"
            >
              Exportar
            </Button>
          </>
        }
      >
        <SearchInput
          variant="bare"
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Buscar por orden, referencia o proveedor..."
          testId="receipts-search"
        />

        {showClient && (
          <FilterableSelect
            label="Cliente:"
            value={filters.company}
            onChange={(company) => dispatch(setReceiptFilters({ company }))}
            options={[{ value: ALL_FILTER, label: 'Todos' }, ...clientOptions]}
            searchPlaceholder="Buscar cliente..."
            panelMinWidth={240}
            testId="receipts-client"
          />
        )}

        <FilterableSelect
          label="Origen:"
          value={filters.source}
          onChange={(source) => dispatch(setReceiptFilters({ source }))}
          options={[{ value: ALL_FILTER, label: 'Todos' }, ...RECEIPT_SOURCE_OPTIONS]}
          panelMinWidth={200}
          testId="receipts-source-filter"
        />
      </FilterBar>

      <DataTable
        columns={columns}
        data={receipts}
        totalItems={totalCount}
        currentPage={filters.page}
        itemsPerPage={filters.rowsPerPage}
        isLoading={loading}
        error={error}
        onRetry={() => dispatch(fetchReceipts())}
        itemLabel="recepciones"
        emptyMessage={emptyMessage}
        onPageChange={(page) => dispatch(setReceiptFilters({ page }))}
        onRowsPerPageChange={(rowsPerPage) => dispatch(setReceiptFilters({ rowsPerPage }))}
      />

      <DeclareReceiptModal
        isOpen={isDeclaring}
        onClose={() => setIsDeclaring(false)}
        warehouse={warehouse}
        receipt={null}
        clientOptions={clientOptions}
        onSaved={(message) => setNotice({ type: 'success', message })}
      />

      <ReceiptsUploadModal
        isOpen={isUploading}
        onClose={() => setIsUploading(false)}
        warehouse={warehouse}
        clientOptions={clientOptions}
        onDone={(message) => setNotice({ type: 'success', message })}
      />

      <ReceiveReceiptModal
        receipt={receiving}
        onClose={() => setReceiving(null)}
        onDone={setNotice}
      />

    </div>
  );
};
